"use client";

import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import { Event } from "@/app/services/eventService";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./calendar-custom.css";

const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface EventCalendarProProps {
  events: Event[];
  onEventClick: (event: Event) => void;
}

export default function EventCalendarPro({
  events,
  onEventClick,
}: EventCalendarProProps) {
  // Transform events for calendar
  const calendarEvents = events.map((event) => {
    const startDateTime = new Date(
      `${event.event_date_start}T${event.event_duration_from}`,
    );
    const endDateTime = new Date(
      `${event.event_date_end}T${event.event_duration_to}`,
    );

    // Check if multi-day event
    const isMultiDay = event.event_date_start !== event.event_date_end;

    return {
      id: event.id,
      title: event.event_name,
      start: startDateTime,
      end: endDateTime,
      resource: event,
      allDay: isMultiDay, // Multi-day events shown as all-day
    };
  });

  // Handle event selection
  const handleSelectEvent = (calendarEvent: any) => {
    onEventClick(calendarEvent.resource);
  };

  // Custom event styling
  const eventStyleGetter = (event: any) => {
    return {
      style: {
        backgroundColor: "#7cb342",
        borderRadius: "4px",
        opacity: 0.95,
        color: "white",
        border: "none",
        display: "block",
      },
    };
  };

  // Custom formats for better display
  const formats = {
    // Month view
    dateFormat: "d",
    dayFormat: "EEE",

    // Week/Day view
    timeGutterFormat: "h:mm a",
    eventTimeRangeFormat: ({ start, end }: any, culture: any, localizer: any) =>
      `${localizer.format(start, "h:mm a", culture)} - ${localizer.format(
        end,
        "h:mm a",
        culture,
      )}`,

    // Agenda view
    agendaDateFormat: "EEE MMM dd",
    agendaTimeFormat: "h:mm a",
    agendaTimeRangeFormat: (
      { start, end }: any,
      culture: any,
      localizer: any,
    ) =>
      `${localizer.format(start, "h:mm a", culture)} - ${localizer.format(
        end,
        "h:mm a",
        culture,
      )}`,

    // Day header format
    dayHeaderFormat: "EEEE MMM dd",
    dayRangeHeaderFormat: ({ start, end }: any, culture: any, localizer: any) =>
      `${localizer.format(start, "MMM dd", culture)} – ${localizer.format(
        end,
        "dd",
        culture,
      )}`,
  };

  // Custom messages
  const messages = {
    date: "Date",
    time: "Time",
    event: "Event",
    allDay: "All Day",
    week: "Week",
    work_week: "Work Week",
    day: "Day",
    month: "Month",
    previous: "Back",
    next: "Next",
    yesterday: "Yesterday",
    tomorrow: "Tomorrow",
    today: "Today",
    agenda: "Agenda",
    noEventsInRange: "No events scheduled for this period.",
    showMore: (total: number) => `+${total} more`,
  };

  return (
    <div
      className="bg-card rounded-2xl p-4 sm:p-6 border border-border shadow-lg"
      style={{
        height: "750px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Calendar
        localizer={localizer}
        events={calendarEvents}
        startAccessor="start"
        endAccessor="end"
        style={{
          height: "100%",
          flex: 1,
        }}
        views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
        defaultView={Views.MONTH}
        onSelectEvent={handleSelectEvent}
        eventPropGetter={eventStyleGetter}
        formats={formats}
        messages={messages}
        popup
        selectable
        step={30}
        timeslots={2}
        showMultiDayTimes
        tooltipAccessor={(event: any) => event.title}
      />
    </div>
  );
}
